import React, { useCallback } from 'react';

import { Box, Icon, OnClick } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ActionButton, { ActionButtonVariants, ActionLink } from 'components/action-button';
import { ApplePass } from 'components/apple-pass';
import { GooglePass } from 'components/google-pass';
import { useSetLoyaltyCardRewardsTierMutation } from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import useEffectOnce from 'hooks/use-effect-once';
import { generateCardIdLabel } from 'pages/account/tims-rewards/details';
import { RewardsStyleVariant } from 'pages/account/tims-rewards/reward-categories/types';
import { RewardsDropdown } from 'pages/cart/cart-reward-dropdown';
import { useCustomizableContent } from 'state/customizable-content';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { routes } from 'utils/routing';

import QRCode from '../../qrcode';

import { LoadingIndicator } from './loading-indicator';
import RewardsUnavailable from './rewards-unavailable';
import {
  ContentBlock,
  HeaderContainer,
  Heading,
  MobileOnlyContent,
  Separator,
  SubHeading,
} from './styled';

interface LoyaltyRewards {
  onDismiss: OnClick;
}

//type OnLinkClick = (e: React.SyntheticEvent<HTMLAnchorElement>) => void;

const PassWrap = styled.div`
  height: 34px;
  display: flex;
  justify-content: center;
  margin-bottom: calc(env(safe-area-inset-bottom) + 100px);
`;

const QRCodeContainer = styled.div`
  border-radius: 10px;
  background: white;
  padding: 2px;
  display: flex;
`;

const QRCodeWrapper = styled.div`
  position: relative;
  border-radius: 6px;
  background: white;
  display: flex;
`;

const RewardsWrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  flex: 1 1 auto;
  justify-content: space-between;
  height: 100%;
`;

const CardNumber = styled.p`
  margin-top: 1.5rem;
  margin-bottom: 0;
`;

const StyledGooglePass = styled(GooglePass)`
  position: relative;
  left: 10px;
`;
const barcodeOptions = {
  color: {
    dark: '#000000FF',
    light: '#FFFFFFFF',
  },
};
const LinkItem = ({ onClick, label, url }: { onClick: OnClick; label: string; url: string }) => {
  return (
    <Box margin="0.5rem" padding="20px 0">
      <ActionLink
        variant={ActionButtonVariants.TEXT_ONLY}
        color="white"
        to={url}
        onClick={onClick}
        endIcon={<Icon icon="next" color="white" aria-hidden />}
      >
        {label}
      </ActionLink>
    </Box>
  );
};

export const ScanRewards: React.FC<LoyaltyRewards> = ({ onDismiss }): JSX.Element => {
  const { formatMessage } = useIntl();
  const mParticle = useMParticleContext();
  const { activeCard, loading } = useLoyaltyActiveCard();
  const { requestLoyaltyCustomizableContent } = useCustomizableContent();
  const navigate = useNavigate();
  const [
    setLoyaltyCardRewardsTier,
    { loading: loadingTier },
  ] = useSetLoyaltyCardRewardsTierMutation();

  useEffectOnce(requestLoyaltyCustomizableContent);

  const handleGoToRoute = useCallback(
    (route: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onDismiss(e);
      navigate(route, { replace: true });
      if (route === routes.addCard) {
        mParticle.logNavigationClick(CustomEventNames.BUTTON_CLICK_REGISTER_PHYSICAL_CARD);
      }
    },
    [onDismiss, navigate, mParticle]
  );

  return (
    <RewardsWrapper>
      <RewardsUnavailable />
      <ContentBlock margin="0">
        <RewardsDropdown
          radioButtonName="scan-page-rewards-dropdown-reward-tier"
          setSelectedRewardsTier={setLoyaltyCardRewardsTier}
          variant={RewardsStyleVariant.Secondary}
        />
      </ContentBlock>
      <MobileOnlyContent>
        <ContentBlock>
          {loading || loadingTier ? (
            <LoadingIndicator />
          ) : (
            <>
              <HeaderContainer>
                <Heading>{formatMessage({ id: 'scanForTimsRewards' })}</Heading>
                <SubHeading>{formatMessage({ id: 'putThisCodeUnderTheScanner' })}</SubHeading>
              </HeaderContainer>
              <QRCodeWrapper>
                <QRCodeContainer>
                  <QRCode
                    barcode={activeCard ? `${activeCard.barcode}|SR` : ''}
                    options={barcodeOptions}
                  />
                </QRCodeContainer>
              </QRCodeWrapper>
            </>
          )}
          {activeCard && <CardNumber>{generateCardIdLabel(activeCard.cardId)}</CardNumber>}
          <Box margin="1.5rem 0 0 0">
            <ActionButton variant={ActionButtonVariants.INVERSE} onClick={onDismiss}>
              {formatMessage({ id: 'close' })}
            </ActionButton>
          </Box>
        </ContentBlock>
      </MobileOnlyContent>
      <ContentBlock margin="0 0 3rem">
        <Separator margin="0" />
        <LinkItem
          onClick={handleGoToRoute(routes.timsRewards)}
          url={routes.timsRewards}
          label={formatMessage({ id: 'registerYourPhysicalCard' })}
        />
        <MobileOnlyContent>
          <PassWrap>
            <ApplePass />
            <StyledGooglePass />
          </PassWrap>
        </MobileOnlyContent>
      </ContentBlock>
    </RewardsWrapper>
  );
};
